import React, { useState, useRef } from 'react';
import { Button, ProgressBar, Alert, ListGroup } from 'react-bootstrap';
import { FaUpload, FaTrash, FaFile, FaFileImage, FaFilePdf, FaFileWord, FaFileExcel, FaFileArchive, FaFileCode } from 'react-icons/fa';
import './FileUploader.scss';

const FileUploader = ({ files, setFiles, maxFiles = 10, maxSize = 50 * 1024 * 1024 }) => {
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  
  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Validate files
    const validFiles = [];
    const errors = [];
    
    for (const file of selectedFiles) {
      // Check if file is valid
      if (!file || !file.name) {
        errors.push('Some files are invalid or corrupted.');
        continue;
      }
      
      // Check file size
      if (file.size > maxSize) {
        errors.push(`File "${file.name}" exceeds the maximum size of ${formatFileSize(maxSize)}.`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    // Check total file count
    if (files.length + validFiles.length > maxFiles) {
      errors.push(`You can only upload a maximum of ${maxFiles} files.`);
      return;
    }
    
    // Show errors if any
    if (errors.length > 0) {
      setError(errors.join(' '));
      return;
    }
    
    // Process valid files
    const newFiles = validFiles.map(file => {
      // Preserve the original File object and add custom properties
      const processedFile = Object.assign(file, {
        // Add our custom properties without destroying the File object
        id: `${file.name || 'unnamed'}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        progress: 0,
        uploaded: false,
        error: null
      });
      
      return processedFile;
    });
    
    setFiles([...files, ...newFiles]);
    setError('');
    
    // Reset the file input
    e.target.value = null;
  };
  
  const updateFileProgress = (fileId, progress) => {
    setFiles(prevFiles => 
      prevFiles.map(file => 
        file.id === fileId 
          ? { 
              ...file, 
              progress: Math.round((progress.loaded / progress.total) * 100),
              uploaded: progress.loaded === progress.total
            } 
          : file
      )
    );
  };
  
  const updateFileError = (fileId, error) => {
    setFiles(prevFiles => 
      prevFiles.map(file => 
        file.id === fileId 
          ? { ...file, error: error, progress: 0 } 
          : file
      )
    );
  };
  
  const removeFile = (fileId) => {
    setFiles(files.filter(file => file.id !== fileId));
  };
  
  const formatFileSize = (bytes) => {
    if (bytes === undefined || bytes === null || isNaN(bytes)) return 'Unknown size';
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  const getFileIcon = (file) => {
    // Check if file or file.name is undefined
    if (!file || !file.name) {
      return <FaFile className="file-icon" />;
    }
    
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    
    if (file.type && file.type.startsWith('image/')) return <FaFileImage className="file-icon" />;
    if (extension === 'pdf') return <FaFilePdf className="file-icon" />;
    if (['doc', 'docx'].includes(extension)) return <FaFileWord className="file-icon" />;
    if (['xls', 'xlsx', 'csv'].includes(extension)) return <FaFileExcel className="file-icon" />;
    if (['zip', 'rar', 'tar', 'gz'].includes(extension)) return <FaFileArchive className="file-icon" />;
    if (['js', 'py', 'java', 'html', 'css', 'php', 'c', 'cpp', 'h', 'rb', 'json', 'ts', 'jsx', 'tsx'].includes(extension)) {
      return <FaFileCode className="file-icon" />;
    }
    
    return <FaFile className="file-icon" />;
  };
  
  // Expose progress update function to parent components
  React.useEffect(() => {
    // Add progress callback to each file
    const updatedFiles = files.map(file => ({
      ...file,
      onProgress: (progress) => updateFileProgress(file.id, progress),
      onError: (error) => updateFileError(file.id, error)
    }));
    
    if (JSON.stringify(updatedFiles) !== JSON.stringify(files)) {
      setFiles(updatedFiles);
    }
  }, [files.length]); // Only run when file count changes
  
  return (
    <div className="file-uploader">
      <div className="upload-area">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          multiple
          style={{ display: 'none' }}
          accept="*/*"
        />
        <Button 
          variant="outline-primary" 
          onClick={() => fileInputRef.current.click()}
          className="upload-button"
          disabled={files.length >= maxFiles}
        >
          <FaUpload className="me-2" />
          Select Files
        </Button>
        <div className="upload-info">
          <small>
            Maximum {maxFiles} files, up to {formatFileSize(maxSize)} each
          </small>
        </div>
      </div>
      
      {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
      
      {files.length > 0 && (
        <ListGroup className="mt-3 file-list">
          {files.map(file => (
            <ListGroup.Item 
              key={file.id} 
              className={`file-item ${file.progress > 0 && file.progress < 100 ? 'uploading' : ''}`}
            >
              <div className="file-info">
                {getFileIcon(file)}
                <div className="file-details">
                  <div className="file-name">{file.name || 'Unnamed file'}</div>
                  <div className="file-size">{formatFileSize(file.size)}</div>
                  {file.error && (
                    <div className="file-error text-danger">
                      <small>Error: {file.error}</small>
                    </div>
                  )}
                </div>
              </div>
              <div className="file-actions">
                {file.progress > 0 && file.progress < 100 ? (
                  <ProgressBar 
                    now={file.progress} 
                    label={`${file.progress}%`} 
                    className="file-progress" 
                  />
                ) : file.uploaded ? (
                  <span className="text-success">
                    <small>✓ Uploaded</small>
                  </span>
                ) : (
                  <Button 
                    variant="link" 
                    className="remove-file" 
                    onClick={() => removeFile(file.id)}
                    title="Remove file"
                  >
                    <FaTrash />
                  </Button>
                )}
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </div>
  );
};

export default FileUploader;
